// Sin el default `React`: el proyecto usa el runtime automático de JSX y tsc
// rechaza el import sin usar (TS6133).
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertIcon } from '../../../assets/icons/index.tsx';

interface Props {
  children: ReactNode;
  /**
   * Cambiar este valor descarta el error y vuelve a montar los hijos. Se le
   * pasa la sección actual: al navegar a otra, la app se recupera sola.
   */
  resetKey?: unknown;
}

interface State {
  error: Error | null;
}

/**
 * Aísla el fallo de una sección para que no tumbe la aplicación entera.
 *
 * React desmonta **todo el árbol** cuando un render lanza y nadie lo captura.
 * Sin una barrera, un error al pintar la rutina dejaba la pantalla en blanco:
 * sin cabecera y sin navegación, así que tampoco se podía ir a otra sección —
 * la única salida era recargar la página.
 *
 * Tiene que ser una clase: los hooks no pueden capturar errores de render.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prevProps: Props) {
    // Al cambiar de sección se reintenta: si el fallo era de esa pantalla, la
    // nueva se monta limpia en vez de heredar el error de la anterior.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Error no controlado en la interfaz:', error, info.componentStack);
  }

  private reintentar = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="text-center p-8">
        <div className="text-red-500 mb-4 flex justify-center">
          <AlertIcon size={40} />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          Esta sección no se ha podido mostrar
        </h3>
        <p className="text-gray-400 mb-4">
          El resto de la aplicación sigue funcionando: puedes cambiar de sección
          o volver a intentarlo.
        </p>
        <button
          onClick={this.reintentar}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-[#1f9e3f] text-white hover:bg-[#1a8636] transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
